import { PanelGroup } from '@hai/ui-react';
import React, { useState } from 'react';
import { Subject } from 'rxjs';
import { FormAggregatorClientInterface } from 'src/js/component/multiple-form-aggregator';
import { DecoderItemFetch } from 'src/js/pages/dashboard/decoder-item-fetch';
import { DecoderViewModel } from 'src/js/pages/streaming/decoder/decoder-model';
import { generateDataAuto } from 'src/js/util/automation';
import { DecoderResetAllStatsModal } from './decoder-reset-all-stats-modal';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import decoderModels, { resetDecoderStatistics } from '../streaming/decoder/decoder-store';
import streamModels, { resetStreamStatistics } from '../streaming/stream/stream-store';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { NotificationVariant } from 'src/js/notification/notification';
import { useWatchObject } from 'src/js/store/use-watch';
import { useTranslation } from 'react-i18next';

interface DecoderListProps {
  data: DecoderViewModel[];
  onClickGlobalSave: Subject<string>;
  formAggregator: FormAggregatorClientInterface;
}

export const DecoderList: React.FunctionComponent<DecoderListProps> = ({ data, onClickGlobalSave, formAggregator }) => {
  const { t } = useTranslation();
  const [streams, setStreams] = useState(streamModels);
  useWatchObject(streamModels, setStreams);

  const [resetAllStatsShown, showResetAllStats, hideResetAllStats] = useModalControls();
  const handleResetAllStatistics = () => {
    hideResetAllStats();
    Promise.all(decoderModels.map((e) => resetDecoderStatistics(e, t))).then((responses) => {
      const success = responses.filter((r) => r.ok);
      if (success.length === decoderModels.length) {
        Promise.all(
          decoderModels.map((e) =>
            resetStreamStatistics(
              streams.find((s) => s.decoderId === e.id),
              t,
            ),
          ),
        ).then((responses) => {
          const success = responses.filter((r) => r.ok);
          if (success.length === decoderModels.length) {
            createAndDispatchNotification(
              t('decoder.notifications.resetAllDecoderSuccess'),
              NotificationVariant.SUCCESS,
              t,
            );
          }
        });
      }
    });
  };

  return (
    <>
      <PanelGroup className="grid-4x">
        {data.map((decoderViewModel: DecoderViewModel) => (
          <DecoderItemFetch
            id={decoderViewModel.id}
            data-auto={generateDataAuto('decoder', `${decoderViewModel.id}`)}
            key={`decoder-${decoderViewModel.id}`}
            onClickGlobalSave={onClickGlobalSave}
            formAggregator={formAggregator}
            showResetAllStats={showResetAllStats}
          />
        ))}
      </PanelGroup>
      {resetAllStatsShown && (
        <DecoderResetAllStatsModal onCancel={hideResetAllStats} onAccept={handleResetAllStatistics} />
      )}
    </>
  );
};
